// Bootstrap Utilities - утилиты для ожидания готовности DOM и Canvas
import { logger } from '@/engine/logging';
import { eventBus } from '@/engine/events/EventBus';

export class BootstrapUtils {
  /**
   * Ожидание появления Canvas элемента с retry логикой
   */
  public static async waitForCanvas(
    canvasId: string,
    maxRetries = 10,
    delay = 100
  ): Promise<HTMLCanvasElement | null> {
    for (let i = 0; i < maxRetries; i++) {
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
      if (canvas) {
        logger.info(`✅ Canvas found after ${i + 1} attempts`, { source: 'BootstrapUtils' });
        return canvas;
      }

      logger.debug(`⏳ Canvas not found, retry ${i + 1}/${maxRetries}...`, {
        source: 'BootstrapUtils',
      });
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    logger.error(
      `❌ Canvas #${canvasId} not found after ${maxRetries} attempts`,
      new Error('Canvas not found'),
      { source: 'BootstrapUtils' }
    );
    return null;
  }

  /**
   * Ожидание события app-ready с таймаутом
   */
  public static async waitForAppReady(timeoutMs = 2000): Promise<boolean> {
    return new Promise(resolve => {
      let completed = false;

      const handler = () => {
        if (completed) return;
        completed = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        eventBus.off('app-ready', handler);
        logger.info('✅ App-ready event received', { source: 'BootstrapUtils' });
        resolve(true);
      };

      const timeoutId =
        timeoutMs > 0
          ? setTimeout(() => {
              if (completed) return;
              completed = true;
              eventBus.off('app-ready', handler);
              logger.warn(
                '⚠️ App-ready event not received within timeout, continuing with fallback',
                { source: 'BootstrapUtils' }
              );
              resolve(false);
            }, timeoutMs)
          : null;

      eventBus.on('app-ready', handler);
    });
  }

  /**
   * Комплексное ожидание Canvas с fallback на app-ready
   */
  public static async waitForCanvasWithFallback(
    canvasId: string
  ): Promise<HTMLCanvasElement | null> {
    // Сначала проверяем Canvas сразу
    let canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;

    if (canvas) {
      logger.info('✅ Canvas found immediately, skipping app-ready wait', {
        source: 'BootstrapUtils',
      });
      return canvas;
    }

    logger.warn('⚠️ Canvas not found immediately, waiting for app-ready...', {
      source: 'BootstrapUtils',
    });

    // Ждем app-ready
    const appReadyReceived = await this.waitForAppReady();

    if (!appReadyReceived) {
      logger.info('🔁 App-ready not received, performing short fallback delay', {
        source: 'BootstrapUtils',
      });
      await new Promise(resolve => setTimeout(resolve, 150));
    }

    // Повторная проверка Canvas после app-ready с retry
    return await this.waitForCanvas(canvasId);
  }

  /**
   * Проверка готовности DOM
   */
  public static isDOMReady(): boolean {
    return document.readyState !== 'loading';
  }

  /**
   * Ожидание готовности DOM
   */
  public static async waitForDOMReady(): Promise<void> {
    if (this.isDOMReady()) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      const handler = () => {
        document.removeEventListener('DOMContentLoaded', handler);
        resolve();
      };
      document.addEventListener('DOMContentLoaded', handler, { once: true });
    });
  }
}
